import { GridChartsModule } from '@ag-grid-enterprise/charts';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ColDef, IRowNode } from '@ag-grid-community/core';
import { SetFilterModule } from "@ag-grid-enterprise/set-filter";
import { debounce } from 'lodash-es';

import { BodySmall, Box, Button, SearchIcon, Snackbar, TableHeaderMenuIcon, TextField, Tooltip } from '@parspec/pixel';
import CustomToolBarPanel from '@parspec/pixel/dist/esm/components/AgGridTable/CustomToolbarPanel';

import { useGetUserProfileInfoQuery } from 'src/features/Settings/MyProfile/queries';
import DeleteConfirmationDialog from 'src/features/shared/DeleteConfirmationDialog';
import { useSnackbar } from 'src/features/shared/hooks/useSnackbar';
import { ManageColumnsPopup } from 'src/features/shared/ManageColumnsPopup/ManageColumnsPopup';
import { useGetSelectedColumnsQuery, useUpdateSelectedColumnsMutation } from 'src/features/shared/ManageColumnsPopup/queries';
import { EVENT_SERVICE_PAGENAME_VALUES } from 'src/features/shared/utils/constants';
import { LoadingOverlayComponent, NoRowsOverlayComponent } from '../../BOM/shared/commonTemplates';
import { DELETE_IMPORTED_RECORDS_TITLE, FIXED_COL_SET, PRICING_HISTORY_COL_DEF, PRICING_HISTORY_TABLE_COLUMN_OPTIONS, PROJECT_SOURCE_LABEL_ENUM } from '../constant';
import { useDeletePricingHistoryMutation, usePricingHistoryMutation } from '../queries';
import UploadHistory from '../UploadHistoryModal';
import { getAllColDef, getDefaultVisibleColDef, sourceCol } from './Columns';
import { MenuOption } from './components';
import { MenuContext } from './context';
import { PricingHistoryTableDataType } from './types';
import { getDeleteDialogSubtext, getDeletingDialogText, getPricingHistoryTableData } from './utils';

interface PricingBodyProps {
    manufacturer?: string;
    modelNo?: string;
    onSelectionChange?: (selectedRow: PricingHistoryTableDataType[]) => void;
    isForPricingHistoryTab?: boolean;
}
const defaultColDef: ColDef = {
    flex: 1,
    resizable: true,
    suppressFillHandle: true,
    suppressMenu: true,
    sortable: false,
    unSortIcon: true,
    menuTabs: ['filterMenuTab'],
    wrapHeaderText: true,
};

export default function PricingHistoryBody({ manufacturer, modelNo, onSelectionChange, isForPricingHistoryTab = false }: PricingBodyProps) {
    const [manufacturerSearchVal, setManufacturerSearchVal] = useState(manufacturer || '');
    const [modelNoSearchVal, setModelNoSearchVal] = useState(modelNo || '');
    const [adhocSearchVal, setAdhocSearchVal] = useState('');
    const [gridReady, setGridReady] = useState(false);
    const [isAnyFilterApplied, setIsAnyFilterApplied] = useState(false);
    const { data: userData, isLoading: isUserDataLoading } = useGetUserProfileInfoQuery();

    const { mutateAsync: postPricingHistory, isLoading } = usePricingHistoryMutation();
    const [pricingHistoryData, setPricingHistoryData] = useState<PricingHistoryTableDataType[] | null>(null);
    const [selectedHistory, setSelectedHistory] = useState<PricingHistoryTableDataType[]>([]);
    const [deleteIds, setDeleteIds] = useState<string[]>([]);
    const { data: userProfile } = useGetUserProfileInfoQuery();
    const isAdmin = userProfile?.data?.role === 'admin';
    const [showImportModal, setShowImportModal] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [openManageColumnsPopup, setOpenManageColumnsPopup] = useState<boolean>(false);
    const { mutateAsync: updateSelectedColumnsMutation, isLoading: isUpdateSelectedColumnsMutationLoading } = useUpdateSelectedColumnsMutation();

    const { mutateAsync: deleteHistory, isLoading: deleteLoading } = useDeletePricingHistoryMutation();
    const { data: selectedColumnsQueryData, isFetching: isSelectedColumnsQueryDataFetching } = useGetSelectedColumnsQuery(
        { userId: userData?.data?.id || 0, pageName: EVENT_SERVICE_PAGENAME_VALUES.PRICING_HISTORY },
        { enabled: !isUserDataLoading }
    );

    const { snackbarInfo, setSnackbarOpen, setSnackbarClose } = useSnackbar();

    const tableRef = useRef<any>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const allColDef = getAllColDef;

    const handleCheckboxChange = (params: any) => {
        if (params.node.selected) {
            setSelectedHistory(prev => [...prev, params?.data])
        } else {
            setSelectedHistory(prev => prev.filter(record => record.uid != params?.data?.uid))
        }
    };

    const checkBoxForPricingHistoryTab = useMemo(() => ({
        headerCheckboxSelection: true,
        checkboxSelection: true,
        minWidth: 50,
        width: 50,
        maxWidth: 50,
        resizable: true,
        colId: 'checkbox',
        hide: !isAdmin
    }), [isAdmin]);

    const lockedColDef = useMemo(() => {
        return isForPricingHistoryTab ? [
            checkBoxForPricingHistoryTab,
            sourceCol] : [sourceCol]
    }, [isForPricingHistoryTab, isAdmin])


    const menuItems = useMemo(() => {
        return {
            minWidth: 60,
            maxWidth: 60,
            headerComponent: () => (
                <Box mt={1}>
                    <TableHeaderMenuIcon color="secondary" fontSize="medium" />
                </Box>
            ),
            cellStyle: { backgroundColor: 'unset' },
            cellRenderer: MenuOption,
            editable: false,
            pinned: 'right',
            colId: 'menu',
            resizable: false,
            lockPosition: 'right',
            lockPinned: true
        }
    }, []);

    const defaultVisibleColDef = getDefaultVisibleColDef;

    const columnDefs: ColDef[] = useMemo(
        () => {
            if (!selectedColumnsQueryData?.table_settings || isSelectedColumnsQueryDataFetching) {
                return isForPricingHistoryTab && isAdmin ? [...lockedColDef, menuItems] : [...lockedColDef];
            }
            else if ((selectedColumnsQueryData?.table_settings?.[0]?.order || [])?.length === 0) {
                //return columns to be shown without any preference set for user
                return isForPricingHistoryTab && isAdmin ? [checkBoxForPricingHistoryTab, ...defaultVisibleColDef, menuItems] : [checkBoxForPricingHistoryTab, ...defaultVisibleColDef];
            }
            else {
                //return columns based on preference
                const orderedColumns = selectedColumnsQueryData?.table_settings?.[0]?.order?.reduce((result: ColDef[], field: string) => {
                    const visibileCol = allColDef.find((col) => col.field === field)
                    if (visibileCol) {
                        result.push(visibileCol)
                    }
                    return result;
                }, []);
                return isForPricingHistoryTab && isAdmin ? [checkBoxForPricingHistoryTab, ...orderedColumns, menuItems] : [checkBoxForPricingHistoryTab, ...orderedColumns];
            }
        },
        [lockedColDef, allColDef, checkBoxForPricingHistoryTab, defaultVisibleColDef, isSelectedColumnsQueryDataFetching, selectedColumnsQueryData, isAdmin, menuItems, isForPricingHistoryTab]
    );
    const searchDebounceRef = useRef({
        handleSearch: debounce(handleSearch, 250)
    });
    const isInitSearchDone = useRef(false);

    async function handleSearch(manufacturer: string, modelNo: string) {
        let responseData: PricingHistoryTableDataType[] = [];
        if (abortControllerRef.current?.signal && !abortControllerRef.current.signal.aborted) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        try {
            const pricingHistoryData = await postPricingHistory({ payload: { manufacturer: manufacturer, model_number: modelNo }, controller: abortControllerRef.current });
            responseData = getPricingHistoryTableData(pricingHistoryData.data || []);
            setPricingHistoryData(responseData);
        } catch (error: any) {
            // catch is for catching abort errors
        }
    }

    useLayoutEffect(() => {
        if (!isInitSearchDone.current) {
            handleSearch(manufacturerSearchVal.trim(), modelNoSearchVal.trim()).then(() => {
                isInitSearchDone.current = true;
            });
        }
        if (isInitSearchDone.current) {
            searchDebounceRef.current.handleSearch(manufacturerSearchVal.trim(), modelNoSearchVal.trim());
        }
    }, [manufacturerSearchVal.trim(), modelNoSearchVal.trim()]);

    useEffect(() => {
        if (!gridReady) {
            return;
        }
        if (isLoading) {
            tableRef?.current?.api?.showLoadingOverlay();
        } else if (!isLoading && Array.isArray(pricingHistoryData) && pricingHistoryData.length === 0) {
            tableRef?.current?.api?.showNoRowsOverlay();
        } else {
            tableRef?.current?.api?.hideOverlay();
        }
    }, [isLoading, pricingHistoryData, gridReady]);

    function handleManufacturerChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const name = event.target.name;
        if (name === 'manufacturer') {
            setManufacturerSearchVal(event.target.value);
        } else if (name === 'modelNo') {
            setModelNoSearchVal(event.target.value);
        } else if (name === 'adhoc') {
            setAdhocSearchVal(event.target.value);
        }
    }

    const clearSearchFields = () => {
        setManufacturerSearchVal(() => '');
        setModelNoSearchVal(() => '');
        setAdhocSearchVal(() => '');
    };

    function handleClearFilters() {
        tableRef.current?.api?.setFilterModel(null);
        clearSearchFields();
    }

    const handleClearSelection = () => {
        tableRef.current?.api?.deselectAll();
    };
    const onCloseBanner = () => {
        setSelectedHistory([]);
        handleClearSelection();
    };

    function onFilterChanged() {
        const allFilters = tableRef.current?.api?.getFilterModel();
        setIsAnyFilterApplied(Object.keys(allFilters).length > 0);
    }

    const closeImportModal = () => {
        setShowImportModal(false);
    };

    const onHistoryImport = ({ message = "" }) => {
        setSnackbarOpen(message, 3000);

        closeImportModal();
    };
    const handleSnackbarClose = (_e: Event | React.SyntheticEvent<any, Event>, reason: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarClose();
    };

    const onHistorySelectionChange = (args: any) => {
        if (isForPricingHistoryTab) {
            return;
        } else {
            onSelectionChange?.(tableRef.current?.api?.getSelectedRows());
        }
    };

    const handleDeleteRecords = async () => {
        try {
            const response = await deleteHistory({ payload: { items: deleteIds } });
            if (response.status === 200) {
                setSnackbarOpen("Successfully deleted.", 2000);
                setShowDeleteDialog(false);
                setSelectedHistory([]);
                setDeleteIds([]);
                setPricingHistoryData((prev: PricingHistoryTableDataType[]) => prev?.filter((item) => !deleteIds.includes(item.uid)));
            }
        } catch (err: any) {
            setSnackbarOpen("Some error occured!")
        }
    };
    const handleDeleteSingleRecord = (e: { uid: string }) => {
        setDeleteIds([e.uid])
        setShowDeleteDialog(true);
    };
    const openDeleteDialog = () => {
        setDeleteIds(selectedHistory.map(record => record.uid));
        setShowDeleteDialog(true);
    };

    const isRowSelectable = (params: IRowNode) => params.data.source !== PROJECT_SOURCE_LABEL_ENUM.PARSPEC || !isForPricingHistoryTab;

    const onManageColumnsClick = () => {
        setOpenManageColumnsPopup(true);
    };

    const onColumnSelection = async (rowData: string[]) => {
        const payload: { pageName: 'pricing_history'; columnData: string[]; userId: number } = { pageName: 'pricing_history', columnData: rowData, userId: userData?.data?.id || 0 };
        await updateSelectedColumnsMutation(payload);

        setOpenManageColumnsPopup(() => false);
    };

    return (
        <Box width="100%" height="100%">
            <Box display="flex" justifyContent="space-between" gap={2} paddingBottom={4}>
                <Box display={'flex'} gap={2}>
                    <Box width={250}>
                        <TextField
                            label={isForPricingHistoryTab ? 'Manufacturer' : 'Manufacturer must contain'}
                            size="small"
                            onChange={handleManufacturerChange}
                            value={manufacturerSearchVal}
                            name="manufacturer"
                        />
                    </Box>
                    <Box minWidth={200}>
                        <TextField
                            label={isForPricingHistoryTab ? 'Model Number' : 'Model Number must contain'}
                            size="small"
                            onChange={handleManufacturerChange}
                            value={modelNoSearchVal}
                            name="modelNo"
                        />
                    </Box>
                    <Box width={250}>
                        <TextField value={adhocSearchVal} label="" placeholder="Search..." startIcon={<SearchIcon fontSize="small" />} size="small" onChange={handleManufacturerChange} name="adhoc" />
                    </Box>
                    {(isAnyFilterApplied || manufacturerSearchVal || modelNoSearchVal || adhocSearchVal) && (
                        <Box>
                            <Tooltip title="Clear Filter(s)">
                                <Button onClick={handleClearFilters} variant="text" color="primary">
                                    Clear Filters
                                </Button>
                            </Tooltip>
                        </Box>
                    )}
                    {isForPricingHistoryTab && isAdmin && (
                        <CustomToolBarPanel
                            toolBarPanelOptions={['delete', 'selectedItems']}
                            selectedRowCount={selectedHistory.length}
                            disabledToolBarButton={!selectedHistory.length}
                            onDelete={openDeleteDialog}
                            onCloseBanner={onCloseBanner}
                        />
                    )}
                </Box>
                {<Box display="flex" gap={2}>
                    <Button variant="outlined" color="secondary" onClick={onManageColumnsClick}>
                        Manage Columns
                    </Button>
                    {isAdmin && isForPricingHistoryTab && (
                        <Button variant="contained" onClick={() => setShowImportModal(true)}>
                            Import
                        </Button>
                    )}
                </Box>}
            </Box>
            <Box height="calc(100% - 52.7px)" className="ag-theme-quartz">
                <MenuContext.Provider value={{ onDelete: handleDeleteSingleRecord }}>
                    <AgGridReact
                        ref={tableRef}
                        rowSelection={isForPricingHistoryTab ? 'multiple' : 'single'}
                        quickFilterText={adhocSearchVal}
                        gridOptions={{
                            suppressContextMenu: true,
                            suppressMenuHide: true,
                            suppressMovableColumns: true,
                            enableRangeSelection: true
                        }}
                        defaultColDef={defaultColDef}
                        columnDefs={columnDefs}
                        suppressRowClickSelection={isForPricingHistoryTab ? true : false}
                        rowData={pricingHistoryData}
                        modules={[GridChartsModule, ColumnsToolPanelModule, ClientSideRowModelModule, ClipboardModule, SetFilterModule]}
                        loadingOverlayComponent={LoadingOverlayComponent}
                        noRowsOverlayComponent={(props: any) => <NoRowsOverlayComponent {...props} />}
                        onGridReady={() => setGridReady(true)}
                        onSelectionChanged={onHistorySelectionChange}
                        onFilterChanged={onFilterChanged}
                        isRowSelectable={isRowSelectable}
                        onRowSelected={handleCheckboxChange}
                    />
                </MenuContext.Provider>
            </Box>
            {showImportModal && (
                <UploadHistory
                    open={showImportModal}
                    onClose={closeImportModal}
                    confirmAction={onHistoryImport}
                />
            )}

            <DeleteConfirmationDialog
                title={DELETE_IMPORTED_RECORDS_TITLE}
                open={showDeleteDialog}
                handleDialog={() => setShowDeleteDialog(false)}
                confirmAction={handleDeleteRecords}
                loading={deleteLoading}
            >
                <Box width="40vw" my={4}>
                    <BodySmall limit={false} fontWeight="600" color="text.primary">
                        {getDeletingDialogText(deleteIds)}
                    </BodySmall>
                    <BodySmall limit={false} color="text.primary">
                        {getDeleteDialogSubtext(deleteIds)}
                    </BodySmall>
                </Box>
            </DeleteConfirmationDialog>
            <Snackbar open={snackbarInfo.open} onClose={handleSnackbarClose} message={snackbarInfo.message} autoHideDuration={snackbarInfo.autoHideDuration} />
            {openManageColumnsPopup && (
                <ManageColumnsPopup
                    isOpen={openManageColumnsPopup}
                    setIsOpen={setOpenManageColumnsPopup}
                    allColumnOptions={PRICING_HISTORY_TABLE_COLUMN_OPTIONS.data}
                    setSnackbarData={setSnackbarOpen}
                    onColumnSelection={onColumnSelection}
                    agGridProps={{ columnDefs: PRICING_HISTORY_COL_DEF }}
                    fixedColumns={FIXED_COL_SET}
                    isSelectedColumnsMutationLoading={isUpdateSelectedColumnsMutationLoading}
                    selectedColumnsData={selectedColumnsQueryData?.table_settings?.[0]?.order || defaultVisibleColDef.map(col => col.field)}
                    isSelectedColumnsQueryFetching={isSelectedColumnsQueryDataFetching}
                />
            )}
        </Box>
    );
}
